import * as React from 'react';

import {
  BoldPlugin,
  HeadingPlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
} from '@platejs/basic-nodes/react';
import type { PlateEditor } from 'platejs/react';
import { ParagraphPlugin, Plate, PlateContent, usePlateEditor } from 'platejs/react';

import { ChatEditorMarkdownPlugin, deserializeMarkdownWithMentions } from './ChatEditorMarkdown';
import { MentionKit } from './ChatEditorMention';
import { getIsEditorWithMarkdownApi } from './SelectionMentionPastePlugin';
import { LinkKit, ListKit, ParagraphElement, TableKit } from '../AlineMarkdownEditor';
import { SkillElementPlugin } from '../AlineMarkdownEditor/plugins/skill-entity-kit';

interface ChatMessageStaticRendererProps {
  markdown: string;
  className?: string;
}

/**
 * Renders markdown chat messages as static Plate content.
 * Supports mentions, bold, italic, underline, strikethrough, lists, links, and tables.
 *
 * MentionElements within this renderer read hydrated entities from MentionEntityContext,
 * which is provided by ChatPanel via useMentionEntitiesQuery.
 */
export const ChatMessageStaticRenderer = React.memo(function ChatMessageStaticRenderer({
  className,
  markdown,
}: ChatMessageStaticRendererProps) {
  const editor = usePlateEditor({
    plugins: [
      ParagraphPlugin.withComponent(ParagraphElement),
      BoldPlugin,
      ItalicPlugin,
      UnderlinePlugin,
      StrikethroughPlugin,
      HeadingPlugin,
      ...ListKit,
      ...LinkKit,
      ...TableKit,
      ...MentionKit,
      SkillElementPlugin,
      ChatEditorMarkdownPlugin,
    ],
    value: editor => getMessageValue(editor, markdown),
  });

  /**
   * The initializer above already parsed this markdown. Skipping an unchanged value keeps
   * a thread from re-parsing every message each time its tab is shown again.
   */
  const appliedMarkdownRef = React.useRef(markdown);

  React.useEffect(() => {
    if (appliedMarkdownRef.current === markdown) return;
    appliedMarkdownRef.current = markdown;
    editor.tf.setValue(
      markdown.trim()
        ? getMessageValue(editor, markdown)
        : [{ children: [{ text: '' }], type: 'p' }]
    );
  }, [markdown, editor]);

  return (
    <Plate readOnly editor={editor}>
      <PlateContent className={className} />
    </Plate>
  );
});

// Markdown that fails to parse still shows, as plain text in one paragraph.
function getMessageValue(editor: PlateEditor, markdown: string) {
  const plainText = [{ children: [{ text: markdown }], type: 'p' }];
  if (!getIsEditorWithMarkdownApi(editor)) return plainText;
  try {
    return deserializeMarkdownWithMentions(editor, markdown);
  } catch (error) {
    console.error('[ChatMessageStaticRenderer] Failed to deserialize markdown:', error);
    return plainText;
  }
}
